import React, { useMemo, useState } from 'react';
import { Box, Container, Grid, Alert, Button, Skeleton, Typography } from '@mui/material';
import TimeRangeSelector, { TimeRangeValue } from './TimeRangeSelector';
import StatisticsPerformanceChart from './StatisticsPerformanceChart';
import PerTickerTable from './PerTickerTable';
import { PnlHistogram, HoldTimeChart } from './DistributionCharts';
import { RegimeCard } from './MarketRegimeCharts';
import StatCard from '../../design/StatCard';
import PanelCard from '../../design/PanelCard';
import PnlText from '../../design/PnlText';
import { formatCurrency } from '../../design/format';
import { pnlColors } from '../../design/theme';
import { useStatistics } from '../../hooks/useStatistics';
import { BenchmarkPoint, useBenchmark, useDeposits, usePerTicker, useDistributions, useRegime } from '../../hooks/useStatisticsPanels';
import { useConfiguration } from '../../hooks/useConfiguration';

interface StatisticsProps {
  traderId: number;
}

// The benchmark fetch covers the selected RANGE, which can start long before
// the trader's first trade (All time, or Last year on a younger trader) — and
// SPY % would then be rebased to that distant close. Always clamp the series
// to the trader's own history span so SPY and the trader are measured over
// the same days, starting at 0% together.
export const benchmarkForPeriod = (
  benchmark: BenchmarkPoint[] | null,
  firstHistoryDate: string | undefined
): BenchmarkPoint[] | null => {
  if (!benchmark || !firstHistoryDate) return benchmark;
  return benchmark.filter((b) => b.date >= firstHistoryDate);
};

const Statistics: React.FC<StatisticsProps> = ({ traderId }) => {
  // All time by default, consistent with the home page and the Overview tab.
  const [timeRange, setTimeRange] = useState<TimeRangeValue>({
    preset: 'all',
    startDate: undefined,
    endDate: undefined,
  });

  const startDate = timeRange.startDate;
  const endDate = timeRange.endDate;

  const { kpis, performanceHistory, loading, error, refetch } = useStatistics({ traderId, startDate, endDate });
  const firstHistoryDate = performanceHistory?.data?.[0]?.date;
  // An unbounded start (All time) must not reach yfinance: with no start it
  // returns only ~1 month of closes. Anchor the fetch to the trader's first
  // history date instead; the benchmark refetches once history arrives.
  const { data: benchmark, error: benchmarkError } = useBenchmark('SPY', startDate ?? firstHistoryDate, endDate);
  // Deposits are the ÷-denominator for the period P&L percent (house convention,
  // same as the Overview cards). A failed fetch just omits the subtitle.
  const { data: deposits } = useDeposits(traderId);
  const {
    data: perTicker,
    loading: perTickerLoading,
    error: perTickerError,
    refetch: perTickerRefetch,
  } = usePerTicker(traderId, startDate, endDate);
  const {
    data: distributions,
    loading: distributionsLoading,
    error: distributionsError,
    refetch: distributionsRefetch,
  } = useDistributions(traderId, startDate, endDate);
  const regime = useRegime();
  const { configuration } = useConfiguration(traderId);

  const benchmarkFailed = !!benchmarkError;
  const spySeries = useMemo(
    () => benchmarkForPeriod(benchmark, firstHistoryDate),
    [benchmark, firstHistoryDate]
  );
  const kpiLoading = loading && !kpis;

  const renderKpiStrip = () => {
    if (kpiLoading) {
      return Array.from({ length: 10 }, (_, i) => (
        <Grid item xs={6} sm={4} md={2.4} key={i}>
          <Skeleton variant="rounded" height={96} />
        </Grid>
      ));
    }
    if (!kpis) return null;

    const cards: React.ReactNode[] = [
      <StatCard
        key="pnl"
        title="Realized P&L"
        value={<PnlText value={kpis.total_pnl} format="currency" variant="h5" />}
        subtitle={
          deposits !== null && deposits > 0 ? (
            <Box display="flex" alignItems="baseline" gap={0.5}>
              <PnlText value={(kpis.total_pnl / deposits) * 100} format="percent" variant="body2" />
              <Typography variant="body2" color="text.secondary">of deposits</Typography>
            </Box>
          ) : undefined
        }
      />,
      <StatCard key="roi" title="ROI" value={<PnlText value={kpis.roi_percent} format="percent" variant="h5" />} />,
      <StatCard
        key="winrate"
        title="Win Rate"
        value={`${kpis.win_rate.toFixed(1)}%`}
        subtitle={`${kpis.winning_trades} of ${kpis.winning_trades + kpis.losing_trades} sells`}
      />,
      <StatCard
        key="trades"
        title="Total Trades"
        value={kpis.total_trades}
        subtitle={`${kpis.winning_trades} wins · ${kpis.losing_trades} losses`}
      />,
      <StatCard key="avgwin" title="Avg Win" value={<PnlText value={kpis.average_win} format="currency" variant="h5" />} />,
      <StatCard key="avgloss" title="Avg Loss" value={<PnlText value={kpis.average_loss} format="currency" variant="h5" />} />,
      <StatCard key="best" title="Best Trade" value={<PnlText value={kpis.best_trade} format="currency" variant="h5" />} />,
      <StatCard key="worst" title="Worst Trade" value={<PnlText value={kpis.worst_trade} format="currency" variant="h5" />} />,
      <StatCard
        key="drawdown"
        title="Max Drawdown"
        value={
          kpis.max_drawdown > 0 ? (
            <Typography variant="h5" component="span" sx={{ fontWeight: 800, color: pnlColors.loss }}>
              {`−${formatCurrency(kpis.max_drawdown)}`}
            </Typography>
          ) : (
            '$0.00'
          )
        }
        subtitle="peak to trough"
      />,
      <StatCard
        key="pf"
        title="Profit Factor"
        value={kpis.profit_factor === null ? '—' : kpis.profit_factor.toFixed(2)}
        subtitle="gross wins ÷ losses"
      />,
    ];

    const spyReturn =
      spySeries && spySeries.length >= 2 && spySeries[0].close > 0
        ? ((spySeries[spySeries.length - 1].close - spySeries[0].close) / spySeries[0].close) * 100
        : null;

    cards.push(
      <StatCard
        key="spy"
        title="S&P 500"
        value={spyReturn === null ? '—' : <PnlText value={spyReturn} format="percent" variant="h5" />}
        subtitle="SPY, close to close"
      />,
    );

    return cards.map((card, i) => (
      <Grid item xs={6} sm={4} md={2.4} key={i}>
        {card}
      </Grid>
    ));
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <TimeRangeSelector value={timeRange} onChange={setTimeRange} loading={loading} />

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={refetch}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 4 }}>
        {renderKpiStrip()}
      </Grid>

      <Box sx={{ mb: 4 }}>
        <PanelCard title="Performance">
          <StatisticsPerformanceChart
            history={performanceHistory?.data ?? []}
            benchmark={spySeries}
            benchmarkFailed={benchmarkFailed}
            loading={loading && !performanceHistory}
            deposits={deposits}
          />
        </PanelCard>
      </Box>

      <Grid container spacing={2.5} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <PanelCard title="SPY regime" error={regime.error} onRetry={regime.refetch}>
            <RegimeCard
              index={regime.data?.find((i) => i.symbol === 'SPY')}
              filterEnabled={configuration?.regime_filter_spy_enabled}
              loading={regime.loading}
            />
          </PanelCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <PanelCard title="QQQ regime" error={regime.error} onRetry={regime.refetch}>
            <RegimeCard
              index={regime.data?.find((i) => i.symbol === 'QQQ')}
              filterEnabled={configuration?.regime_filter_qqq_enabled}
              loading={regime.loading}
            />
          </PanelCard>
        </Grid>
      </Grid>

      <Box sx={{ mb: 4 }}>
        <PanelCard title="Per-ticker breakdown" error={perTickerError} onRetry={perTickerRefetch}>
          <PerTickerTable rows={perTicker} loading={perTickerLoading} />
        </PanelCard>
      </Box>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6}>
          <PanelCard title="P&L distribution" error={distributionsError} onRetry={distributionsRefetch}>
            <PnlHistogram
              data={distributions && distributions.total_sells > 0 ? distributions.pnl_histogram : []}
              loading={distributionsLoading}
            />
          </PanelCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <PanelCard title="Hold time" error={distributionsError} onRetry={distributionsRefetch}>
            <HoldTimeChart
              data={distributions && distributions.total_sells > 0 ? distributions.hold_time_distribution : []}
              loading={distributionsLoading}
            />
          </PanelCard>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Statistics;
